/**
 * Navigation: the single source of truth for the sidebar, the sidebar's
 * bottom icons (settings / reports / billing / support), the apps launcher,
 * quick-add, global search, the mobile footer and the landing page.
 *
 * Every destination carries the SAME permission requirement as its route's
 * `meta.permission` (or `access: 'self'`) — rbac/__tests__/navigation.test.ts
 * checks this against the real router, so a visible link never ends on a 403
 * and a hidden page is really closed.
 *
 * Pure: no store, router or i18n imports (the layout binds them through
 * useNavigation.ts). Labels are i18n keys.
 */
import type { RouteLocationRaw } from 'vue-router';
import { isLegacySnapshot, meets } from './can';
import { moduleOf, type PermissionSlug } from './slugs';
import type { PermissionRequirement, PermissionSnapshot, Scope } from './types';

export interface NavItem {
  key: string;
  labelKey: string;
  /** Path of the destination (leaves only). */
  to?: string;
  /** Route name of the destination — landing page candidates need it. */
  routeName?: string;
  /** Icon name understood by DashboardLayout's sidebar. */
  icon: string;
  /** Same requirement as the route's meta.permission. */
  permission?: PermissionRequirement;
  /** The person's own page (profile): any staff member. */
  access?: 'self';
  children?: readonly NavItem[];
  /** Top-level only: 'main' list (default), the bottom 'utility' icons, or not in the sidebar. */
  sidebar?: 'main' | 'utility' | false;
  launcher?: { bg: string; iconPath: string; order: number };
  /** Extra search words (the translated label always matches). */
  search?: readonly string[];
  quickAdd?: { labelKey: string; to: string; permission: PermissionSlug; icon: string };
  mobileFooter?: { order: number; icon: string; labelKey?: string };
}

const ICON = {
  folder: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  tasks: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  book: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  desktop: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  document: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  money: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  people: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  cart: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z',
  cloud: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
  sheet: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
  ticket: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z',
  qr: 'M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 1v2h2V5H5zm-2 9a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zm2 1v2h2v-2H5zm9-11a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zm2 1v2h2V5h-2zm-2 9h2v2h-2v-2zm4 0h2v4h-4v-2h2v-2zm-4 4h2v2h-2v-2z',
  sparkles: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z',
  cog: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  // quick-add
  qaTask: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  qaEmployee: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  qaNotice: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
} as const;

/**
 * The settings page opens with any grant one of its tabs needs
 * (pages/dashboard/settings/shared/settingsAccess.ts): company info, roles,
 * the settings themselves, and the company-wide Leave types / PTO
 * (leaves.approve at 'all') and Knowledge base (knowledge-base.edit at
 * 'all') tabs.
 */
export const SETTINGS_PERMISSION: PermissionRequirement = {
  any: [
    { anyOf: ['settings.view', 'company-info.view', 'settings.roles'] },
    { slug: 'leaves.approve', scope: 'all' },
    { slug: 'knowledge-base.edit', scope: 'all' },
  ],
};

export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: 'dashboard', labelKey: 'nav.dashboard', icon: 'dashboard',
    children: [
      { key: 'home', labelKey: 'nav.privateDashboard', to: '/', routeName: 'dashboard', icon: 'home', permission: 'dashboard.view', search: ['dashboard', 'home', 'overview'], mobileFooter: { order: 1, icon: 'home', labelKey: 'nav.dashboard' } },
      { key: 'advanced-dashboard', labelKey: 'nav.advancedDashboard', to: '/advanced-dashboard', routeName: 'advanced-dashboard', icon: 'chart-bar', permission: 'advanced-dashboard.view', search: ['advanced', 'analytics', 'charts'] },
    ],
  },
  {
    key: 'client-management', labelKey: 'nav.clientManagement', icon: 'users-group',
    children: [
      { key: 'leads', labelKey: 'nav.leads', to: '/leads', routeName: 'leads', icon: 'funnel', permission: 'leads.view', search: ['lead', 'prospect', 'pipeline', 'sales'], launcher: { bg: 'bg-amber-500', iconPath: ICON.cart, order: 12 } },
      { key: 'clients', labelKey: 'nav.clients', to: '/clients', routeName: 'clients', icon: 'users-group', permission: 'clients.view', search: ['client', 'customer', 'account'], launcher: { bg: 'bg-blue-600', iconPath: ICON.people, order: 11 } },
    ],
  },
  {
    key: 'hr', labelKey: 'nav.hr', icon: 'users',
    children: [
      { key: 'employees', labelKey: 'nav.employees', to: '/employees', routeName: 'employees', icon: 'user', permission: 'employees.view', search: ['employee', 'staff', 'team', 'hr', 'people'], quickAdd: { labelKey: 'layout.newEmployee', to: '/employees?action=create', permission: 'employees.create', icon: ICON.qaEmployee } },
      { key: 'departments', labelKey: 'nav.departments', to: '/departments', routeName: 'departments', icon: 'building', permission: 'departments.view', search: ['department', 'division', 'org'] },
      { key: 'designations', labelKey: 'nav.designations', to: '/designations', routeName: 'designations', icon: 'badge', permission: 'designations.view', search: ['designation', 'title', 'position'] },
      { key: 'attendance', labelKey: 'nav.attendance', to: '/attendance', routeName: 'attendance', icon: 'clock', permission: 'attendance.view', search: ['attendance', 'check-in', 'checkin', 'clock'], launcher: { bg: 'bg-indigo-500', iconPath: ICON.clock, order: 3 } },
      { key: 'leaves', labelKey: 'nav.leaves', to: '/leaves', routeName: 'leaves', icon: 'calendar-off', permission: 'leaves.view', search: ['leave', 'vacation', 'time off', 'pto', 'absence'] },
      { key: 'shifts', labelKey: 'nav.shifts', to: '/shifts', routeName: 'shifts', icon: 'refresh', permission: 'shifts.view', search: ['shift', 'schedule', 'rota'], launcher: { bg: 'bg-yellow-500', iconPath: ICON.refresh, order: 4 } },
      { key: 'holidays', labelKey: 'nav.holidays', to: '/holidays', routeName: 'holidays', icon: 'sun', permission: 'holidays.view', search: ['holiday', 'day off', 'public holiday'] },
      { key: 'appreciations', labelKey: 'nav.appreciations', to: '/appreciations', routeName: 'appreciations', icon: 'star', permission: 'appreciations.view', search: ['appreciation', 'reward', 'recognition'] },
    ],
  },
  {
    key: 'work', labelKey: 'nav.work', icon: 'briefcase',
    children: [
      { key: 'projects', labelKey: 'nav.projects', to: '/projects', routeName: 'projects', icon: 'folder', permission: 'projects.view', search: ['project', 'workspace'], launcher: { bg: 'bg-purple-500', iconPath: ICON.folder, order: 1 }, quickAdd: { labelKey: 'layout.newProject', to: '/projects?action=create', permission: 'projects.create', icon: ICON.folder }, mobileFooter: { order: 2, icon: 'projects' } },
      { key: 'tasks', labelKey: 'nav.tasks', to: '/tasks', routeName: 'tasks', icon: 'clipboard-list', permission: 'tasks.view', search: ['task', 'todo', 'work item'], launcher: { bg: 'bg-primary-500', iconPath: ICON.tasks, order: 2 }, quickAdd: { labelKey: 'layout.newTask', to: '/tasks?action=create', permission: 'tasks.create', icon: ICON.qaTask }, mobileFooter: { order: 3, icon: 'tasks' } },
      { key: 'contracts', labelKey: 'nav.contracts', to: '/contracts', routeName: 'contracts', icon: 'document', permission: 'contracts.view', search: ['contract', 'agreement'] },
      { key: 'tdocs', labelKey: 'nav.tdocs', to: '/tdocs', routeName: 'tdocs', icon: 'doc-text', permission: 'tdrive.view', search: ['document', 'doc', 'tdoc', 'write', 'note'], launcher: { bg: 'bg-pink-500', iconPath: ICON.document, order: 14 } },
      { key: 'tsheet', labelKey: 'nav.tsheet', to: '/tsheet', routeName: 'tsheet', icon: 'table-cells', permission: 'tdrive.view', search: ['sheet', 'spreadsheet', 'tsheet', 'excel', 'table'], launcher: { bg: 'bg-lime-600', iconPath: ICON.sheet, order: 15 } },
    ],
  },
  { key: 'tdrive', labelKey: 'nav.tdrive', to: '/tdrive', routeName: 'tdrive', icon: 'cloud', permission: 'tdrive.view', search: ['drive', 'file', 'storage', 'cloud', 'upload'], launcher: { bg: 'bg-violet-500', iconPath: ICON.cloud, order: 13 } },
  {
    key: 'finance', labelKey: 'nav.finance', icon: 'wallet',
    children: [
      { key: 'estimates', labelKey: 'nav.estimates', to: '/estimates', routeName: 'estimates', icon: 'calculator', permission: 'estimates.view', search: ['estimate', 'quote'] },
      { key: 'proposals', labelKey: 'nav.proposals', to: '/proposals', routeName: 'proposals', icon: 'document-text', permission: 'proposals.view', search: ['proposal', 'pitch', 'offer', 'quote', 'sales'], launcher: { bg: 'bg-orange-500', iconPath: ICON.document, order: 9 } },
      { key: 'invoices', labelKey: 'nav.invoices', to: '/invoices', routeName: 'invoices', icon: 'receipt', permission: 'invoices.view', search: ['invoice', 'billing', 'bill'], quickAdd: { labelKey: 'layout.newInvoice', to: '/invoices?action=create', permission: 'invoices.create', icon: ICON.document } },
      { key: 'expenses', labelKey: 'nav.expenses', to: '/expenses', routeName: 'expenses', icon: 'credit-card', permission: 'expenses.view', search: ['expense', 'cost', 'spending'] },
      { key: 'payments', labelKey: 'nav.payments', to: '/payments', routeName: 'payments', icon: 'cash', permission: 'payments.view', search: ['payment', 'pay', 'transaction'] },
      { key: 'bank-accounts', labelKey: 'nav.bankAccounts', to: '/bank-accounts', routeName: 'bank-accounts', icon: 'building-bank', permission: 'bank-accounts.view', search: ['bank', 'account', 'finance'] },
    ],
  },
  { key: 'tickets', labelKey: 'nav.tickets', to: '/tickets', routeName: 'tickets', icon: 'ticket', permission: 'tickets.view', search: ['ticket', 'support', 'help desk', 'issue'], launcher: { bg: 'bg-red-500', iconPath: ICON.ticket, order: 16 } },
  { key: 'events', labelKey: 'nav.events', to: '/calendar', routeName: 'calendar', icon: 'calendar', permission: 'calendar.view', search: ['event', 'calendar', 'meeting', 'schedule'], launcher: { bg: 'bg-blue-500', iconPath: ICON.calendar, order: 5 } },
  { key: 'notices', labelKey: 'nav.noticeBoard', to: '/notices', routeName: 'notices', icon: 'megaphone', permission: 'notices.view', search: ['notice', 'announcement', 'bulletin'], quickAdd: { labelKey: 'layout.newNotice', to: '/notices?action=create', permission: 'notices.create', icon: ICON.qaNotice } },
  { key: 'messages', labelKey: 'nav.messages', to: '/messages', routeName: 'messages', icon: 'chat', permission: 'messages.view', search: ['message', 'chat', 'conversation'], launcher: { bg: 'bg-green-500', iconPath: ICON.chat, order: 6 }, mobileFooter: { order: 4, icon: 'messages' } },
  { key: 'knowledge-base', labelKey: 'nav.knowledgeBase', to: '/knowledge-base', routeName: 'knowledge-base', icon: 'book', permission: 'knowledge-base.view', search: ['knowledge', 'wiki', 'documentation', 'article'], launcher: { bg: 'bg-teal-500', iconPath: ICON.book, order: 7 } },
  { key: 'assets', labelKey: 'nav.assets', to: '/assets', routeName: 'assets', icon: 'desktop', permission: 'assets.view', search: ['asset', 'equipment', 'device', 'inventory'], launcher: { bg: 'bg-cyan-500', iconPath: ICON.desktop, order: 8 } },
  {
    key: 'payroll', labelKey: 'nav.payroll', icon: 'banknotes',
    children: [
      { key: 'payroll-page', labelKey: 'nav.payroll', to: '/payroll', routeName: 'payroll', icon: 'banknotes', permission: 'payroll.view', search: ['payroll', 'salary', 'wage', 'pay'], launcher: { bg: 'bg-emerald-600', iconPath: ICON.money, order: 10 } },
    ],
  },

  // Sidebar bottom icons
  { key: 'settings', labelKey: 'nav.settings', to: '/settings', routeName: 'settings', icon: 'cog', permission: SETTINGS_PERMISSION, sidebar: 'utility', search: ['setting', 'config', 'preference'], launcher: { bg: 'bg-gray-500', iconPath: ICON.cog, order: 19 } },
  { key: 'reports', labelKey: 'nav.reports', to: '/reports', routeName: 'reports', icon: 'chart-bar', permission: 'reports.view', sidebar: 'utility', search: ['report', 'analytics'] },
  { key: 'billing', labelKey: 'nav.billing', to: '/billing', icon: 'credit-card', permission: 'settings.edit', sidebar: 'utility', search: ['billing', 'plan', 'subscription'] },
  { key: 'support', labelKey: 'nav.support', to: '/support', routeName: 'support-tickets', icon: 'question', permission: 'support-tickets.view', sidebar: 'utility', search: ['support', 'help'] },

  // Not in the sidebar (launcher, topbar, search)
  { key: 'qr-generator', labelKey: 'layout.qrGenerator', to: '/qr-generator', routeName: 'qr-generator', icon: 'qr', permission: 'qr-generator.view', sidebar: false, search: ['qr', 'code', 'generate', 'barcode'], launcher: { bg: 'bg-gray-700 dark:bg-gray-600', iconPath: ICON.qr, order: 17 } },
  { key: 'ai-chatbot', labelKey: 'nav.aiChatbot', to: '/ai-chatbot', routeName: 'ai-chatbot', icon: 'sparkles', permission: 'ai-chatbot.view', sidebar: false, search: ['ai', 'assistant', 'agent', 'chatbot'], launcher: { bg: 'bg-gradient-to-br from-primary-500 to-orange-400', iconPath: ICON.sparkles, order: 18 } },
  { key: 'notifications', labelKey: 'layout.notifications', to: '/notifications', routeName: 'notifications', icon: 'bell', permission: 'notifications.view', sidebar: false, search: ['notification', 'alert', 'bell'] },
  { key: 'profile', labelKey: 'profile.title', to: '/profile', routeName: 'profile', icon: 'user', access: 'self', sidebar: false, search: ['profile', 'account', 'password'] },
];

/** Landing page order (blueprint §4); Profile is the last resort and always open to staff. */
export const LANDING_ORDER: readonly string[] = ['home', 'tasks', 'projects', 'messages', 'events', 'leaves', 'attendance', 'tdrive', 'knowledge-base', 'notices'];
export const FALLBACK_LANDING_ROUTE = 'profile';

/** Every destination (leaf), depth first, in declaration order. */
export function flattenNav(items: readonly NavItem[]): NavItem[] {
  return items.flatMap((item) => (item.children ? flattenNav(item.children) : item.to ? [item] : []));
}

/** Leaves of the main sidebar list (top-level 'main' items and their children). */
function mainSidebarLeaves(items: readonly NavItem[]): NavItem[] {
  return flattenNav(items.filter((i) => (i.sidebar ?? 'main') === 'main'));
}

/** Canonical text form of a requirement, for comparing nav items with route meta. */
export function requirementKey(req: PermissionRequirement | readonly PermissionSlug[] | null | undefined): string {
  if (!req) return '';
  if (typeof req === 'string') return req;
  if (Array.isArray(req)) return `any:${[...req].sort().join(',')}`;
  const r = req as Partial<{ anyOf: readonly string[]; allOf: readonly string[]; slug: string; scope: Scope; any: readonly PermissionRequirement[] }>;
  if (r.any) return `or(${r.any.map((branch) => requirementKey(branch)).sort().join('|')})`;
  const at = r.scope ? `@${r.scope}` : '';
  if (r.anyOf) return `any:${[...r.anyOf].sort().join(',')}${at}`;
  if (r.allOf) return `all:${[...r.allOf].sort().join(',')}${at}`;
  return `${r.slug ?? ''}${at}`;
}

// ---------- visibility ----------

/** Company module flags (modules store) — consulted for legacy sessions only. */
export interface ModuleFlags {
  isLoaded: boolean;
  isEnabled: (moduleSlug: string) => boolean;
}

export interface NavContext {
  isStaff: boolean;
  meets: (req: PermissionRequirement) => boolean;
  /** Legacy sessions only: is the item's company module on? */
  moduleEnabled?: (item: NavItem) => boolean;
  translate: (key: string) => string;
}

/** Permission modules that are not company modules (no entitlement), as the sidebar treated them before. */
const ALWAYS_ON_MODULES: ReadonlySet<string> = new Set([
  'dashboard', 'advanced-dashboard', 'notifications', 'company-info', 'settings', 'reports',
  'qr-generator', 'support-tickets', 'ai-chatbot', 'bank-accounts',
]);
/** Permission modules that ride another company module. */
const MODULE_ALIASES: Readonly<Record<string, readonly string[]>> = {
  leads: ['clients'],
  proposals: ['proposals', 'estimates'],
};

function firstSlug(req: PermissionRequirement | undefined): PermissionSlug | null {
  if (!req) return null;
  if (typeof req === 'string') return req;
  if ('any' in req) return firstSlug(req.any[0]);
  if ('anyOf' in req) return req.anyOf[0] ?? null;
  if ('allOf' in req) return req.allOf[0] ?? null;
  return req.slug;
}

function legacyModuleOn(item: NavItem, modules: ModuleFlags): boolean {
  const slug = firstSlug(item.permission);
  if (!slug) return true;
  const module = moduleOf(slug);
  if (ALWAYS_ON_MODULES.has(module)) return true;
  if (!modules.isLoaded) return false;
  return (MODULE_ALIASES[module] ?? [module]).some((m) => modules.isEnabled(m));
}

/**
 * Context for a session. With a current session the permissions already
 * exclude disabled modules (server-side entitlement), so module flags are
 * only consulted for a legacy cached session.
 */
export function navContextFor(snapshot: PermissionSnapshot, translate: (key: string) => string, modules?: ModuleFlags): NavContext {
  return {
    isStaff: snapshot.principal === 'staff',
    meets: (req) => meets(snapshot, req),
    moduleEnabled: modules && isLegacySnapshot(snapshot) ? (item) => legacyModuleOn(item, modules) : undefined,
    translate,
  };
}

function leafVisible(item: NavItem, ctx: NavContext): boolean {
  if (!ctx.isStaff) return false;
  if (item.access === 'self') return true;
  if (!item.permission || !ctx.meets(item.permission)) return false;
  return ctx.moduleEnabled ? ctx.moduleEnabled(item) : true;
}

// ---------- resolved navigation ----------

/** A visible destination with its translated label. */
export interface ResolvedNavLeaf {
  key: string;
  name: string;
  icon: string;
  to: string;
}
/** A sidebar entry: a destination, or a group of destinations (`children`, no `to`). */
export interface ResolvedNavItem {
  key: string;
  name: string;
  icon: string;
  to?: string;
  children?: ResolvedNavLeaf[];
}
export interface LauncherApp { key: string; name: string; to: string; bg: string; iconPath: string }
export interface QuickAddAction { key: string; name: string; to: string; iconPath: string }
export interface FooterTab { key: string; name: string; to: string; icon: string }
export interface SearchResult { key: string; name: string; to: string }

export interface ResolvedNavigation {
  sidebar: ResolvedNavItem[];
  utility: ResolvedNavItem[];
  launcher: LauncherApp[];
  quickAdd: QuickAddAction[];
  mobileFooter: FooterTab[];
  /** Visible destinations as {name, to} — page pin labels. */
  entries: Array<{ name: string; to: string }>;
  isVisible: (key: string) => boolean;
  search: (query: string, limit?: number) => SearchResult[];
}

export function buildNavigation(ctx: NavContext, items: readonly NavItem[] = NAV_ITEMS): ResolvedNavigation {
  const t = ctx.translate;
  const visible = new Set<string>();

  function resolveLeaf(item: NavItem): ResolvedNavLeaf | null {
    if (item.children || !item.to || !leafVisible(item, ctx)) return null;
    visible.add(item.key);
    return { key: item.key, name: t(item.labelKey), icon: item.icon, to: item.to };
  }

  function resolve(item: NavItem): ResolvedNavItem | null {
    if (!item.children) return resolveLeaf(item);
    const children = item.children.map(resolveLeaf).filter((c): c is ResolvedNavLeaf => c !== null);
    if (!children.length) return null;
    visible.add(item.key);
    return { key: item.key, name: t(item.labelKey), icon: item.icon, children };
  }

  const top = items.map((item) => ({ item, resolved: resolve(item) }));
  const placed = (where: 'main' | 'utility') =>
    top.filter(({ item, resolved }) => resolved && (item.sidebar ?? 'main') === where).map(({ resolved }) => resolved!);

  const leaves = flattenNav(items).filter((i) => visible.has(i.key));

  const launcher = leaves
    .filter((i) => i.launcher)
    .sort((a, b) => a.launcher!.order - b.launcher!.order)
    .map((i) => ({ key: i.key, name: t(i.labelKey), to: i.to!, bg: i.launcher!.bg, iconPath: i.launcher!.iconPath }));

  const quickAdd = leaves
    .filter((i) => i.quickAdd && ctx.meets(i.quickAdd.permission))
    .map((i) => ({ key: i.key, name: t(i.quickAdd!.labelKey), to: i.quickAdd!.to, iconPath: i.quickAdd!.icon }));

  const mobileFooter = leaves
    .filter((i) => i.mobileFooter)
    .sort((a, b) => a.mobileFooter!.order - b.mobileFooter!.order)
    .map((i) => ({ key: i.key, name: t(i.mobileFooter!.labelKey ?? i.labelKey), to: i.to!, icon: i.mobileFooter!.icon }));

  const entries = leaves.map((i) => ({ name: t(i.labelKey), to: i.to! }));

  function search(query: string, limit = 8): SearchResult[] {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return leaves
      .filter((i) => t(i.labelKey).toLowerCase().includes(q) || (i.search ?? []).some((k) => k.includes(q)))
      .slice(0, limit)
      .map((i) => ({ key: i.key, name: t(i.labelKey), to: i.to! }));
  }

  return {
    sidebar: placed('main'),
    utility: placed('utility'),
    launcher,
    quickAdd,
    mobileFooter,
    entries,
    isVisible: (key) => visible.has(key),
    search,
  };
}

// ---------- landing ----------

const BY_KEY: ReadonlyMap<string, NavItem> = new Map(flattenNav(NAV_ITEMS).map((i) => [i.key, i]));

/**
 * Route NAME of the page a session should start on: the first permitted page
 * in LANDING_ORDER, then the first permitted main-sidebar page, then Profile.
 * Never a page the person can't open, so redirects can't loop (F23).
 */
export function landingRouteName(snapshot: PermissionSnapshot): string {
  if (snapshot.principal === 'client') return 'client-dashboard';
  if (snapshot.principal === 'superadmin') return 'admin-dashboard';
  if (snapshot.principal !== 'staff') return 'signin';
  const ctx = navContextFor(snapshot, (k) => k);
  for (const key of LANDING_ORDER) {
    const item = BY_KEY.get(key);
    if (item?.routeName && leafVisible(item, ctx)) return item.routeName;
  }
  const first = mainSidebarLeaves(NAV_ITEMS).find((i) => i.routeName && leafVisible(i, ctx));
  return first?.routeName ?? FALLBACK_LANDING_ROUTE;
}

export function landingRoute(snapshot: PermissionSnapshot): RouteLocationRaw {
  return { name: landingRouteName(snapshot) };
}
